import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { ANNUAL_BILLING_ENABLED } from '../lib/features';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Check, Shield, Loader2, Tag, X, CheckCircle, Bitcoin, Award } from 'lucide-react';
import { FreeTierIcon, BronzeTierIcon, SilverTierIcon, GoldTierIcon } from '../components/TierIcons';
import { createSubscriptionCheckout, validateDiscountCode, getPaymentTiers, createCryptoCheckout, createPayPalSubscription, getPayPalConfig } from '../services/api';
import { toast } from '../hooks/use-toast';

// Default tier colors
const TIER_COLORS = {
  free: '#6B7280',
  bronze: '#CD7F32', 
  silver: '#C0C0C0',
  gold: '#FFD700'
};

const SUBSCRIPTION_TIERS_NAMES = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold'
};

const PROMO_STORAGE_KEY = 'pendingPromoCode';

const TiersPage = () => {
  const { currentUser, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCode = (searchParams.get('code') || '').trim().toUpperCase();
  const [loadingTier, setLoadingTier] = useState(null);
  const [discountCode, setDiscountCode] = useState(urlCode || sessionStorage.getItem(PROMO_STORAGE_KEY) || '');
  const [autoApplied, setAutoApplied] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [billingInterval, setBillingInterval] = useState('month');
  const [annualDiscount, setAnnualDiscount] = useState(20);
  const [cryptoLoading, setCryptoLoading] = useState(null);
  const [paypalLoading, setPaypalLoading] = useState(null);
  const [paypalEnabled, setPaypalEnabled] = useState(false);

  const loadTiers = useCallback(async () => {
    try {
      setLoadingTiers(true);
      const response = await getPaymentTiers();
      if (response.annual_discount_percent) {
        setAnnualDiscount(response.annual_discount_percent);
      }
      const tiersArray = Object.entries(response.tiers || {}).map(([key, tier]) => ({
        id: `tier_${key}`,
        tierKey: key,
        name: tier.name,
        price: tier.price,
        annualPrice: tier.price > 0 ? Math.round(tier.price * 12 * (1 - (response.annual_discount_percent || 20) / 100) * 100) / 100 : 0,
        description: tier.description,
        benefits: tier.benefits || [],
        accessLevel: tier.tier_level,
        color: TIER_COLORS[key] || '#F59E0B',
        isFree: tier.price === 0
      })).sort((a, b) => a.accessLevel - b.accessLevel);
      setTiers(tiersArray);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load subscription tiers',
        variant: 'destructive'
      });
    } finally {
      setLoadingTiers(false);
    }
  }, []);

  useEffect(() => {
    loadTiers();
    getPayPalConfig().then(() => setPaypalEnabled(true)).catch(() => setPaypalEnabled(false));
  }, [loadTiers]);

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'Bronze': return <BronzeTierIcon className="w-8 h-8" />;
      case 'Silver': return <SilverTierIcon className="w-8 h-8" />;
      case 'Gold': return <GoldTierIcon className="w-8 h-8" />;
      default: return <FreeTierIcon className="w-8 h-8" />;
    }
  };

  const handleApplyDiscount = async (codeOverride) => {
    const code = (typeof codeOverride === 'string' ? codeOverride : discountCode).trim().toUpperCase();
    if (!code) {
      toast({
        title: 'Enter Code',
        description: 'Please enter a discount code',
        variant: 'destructive'
      });
      return;
    }

    try {
      setValidatingCode(true);
      
      // Validate against all paid tiers, remembering the real rejection reason
      const paidTiers = tiers.filter(t => !t.isFree);
      const discountResults = {};
      let rejectionReason = null;
      
      for (const tier of paidTiers) {
        const tierName = tier.id.replace('tier_', '');
        try {
          const result = await validateDiscountCode(code, tierName, billingInterval);
          if (result.valid) {
            discountResults[tier.id] = {
              code: result.code,
              originalPrice: result.original_price,
              discountedPrice: result.discounted_price,
              savings: result.savings
            };
          }
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (detail && !String(detail).includes('does not apply to')) rejectionReason = detail;
        }
      }
      
      if (Object.keys(discountResults).length > 0) {
        setDiscountCode(code);
        setAppliedDiscount(discountResults);
        sessionStorage.setItem(PROMO_STORAGE_KEY, code);
        const firstSavings = Object.values(discountResults)[0];
        toast({
          title: 'Discount Applied!',
          description: `Code ${firstSavings.code} applied to ${Object.keys(discountResults).length} tier(s)`
        });
      } else {
        toast({
          title: 'Code Not Applied',
          description: rejectionReason || 'This discount code is not valid for any tier',
          variant: 'destructive'
        });
        setAppliedDiscount(null);
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
      }
    } catch (error) {
      toast({
        title: 'Invalid Code',
        description: error.response?.data?.detail || 'This discount code is not valid',
        variant: 'destructive'
      });
      setAppliedDiscount(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleClearDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    sessionStorage.removeItem(PROMO_STORAGE_KEY);
  };

  // Auto-apply a code arriving via /tiers?code=XXX (or restored after login)
  useEffect(() => {
    if (!autoApplied && !loadingTiers && tiers.length > 0 && discountCode.trim()) {
      setAutoApplied(true);
      handleApplyDiscount(discountCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingTiers, tiers]);

  // Re-validate an applied code when switching monthly/annual (monthly-only codes)
  useEffect(() => {
    if (appliedDiscount && discountCode.trim()) {
      handleApplyDiscount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInterval]);

  const goToLoginKeepingCode = () => {
    if (discountCode.trim()) sessionStorage.setItem(PROMO_STORAGE_KEY, discountCode.trim().toUpperCase());
    navigate('/login');
  };

  const handleSubscribe = async (tierId) => {
    if (!currentUser) {
      goToLoginKeepingCode();
      return;
    }

    const tierName = tierId.replace('tier_', '');
    
    if (tierName === 'free') {
      // Never change a member's tier from here. Paying members must cancel from Profile
      // (the processor keeps billing until the subscription itself is cancelled).
      if (subActive && currentUser.subscriptionTier !== 'free') {
        toast({
          title: 'Your membership stays active',
          description: 'Selecting Free does not cancel billing. To stop renewing, use "Cancel membership" on your Profile.'
        });
        navigate('/profile#billing');
        return;
      }
      navigate('/feed');
      return;
    }

    try {
      setLoadingTier(tierId);
      
      // Pass discount code if one is applied for this tier
      const hasDiscount = appliedDiscount && appliedDiscount[tierId];
      const result = await createSubscriptionCheckout(tierName, hasDiscount ? discountCode : null, billingInterval);
      
      // If free upgrade (100% discount), handle directly
      if (result.free_upgrade) {
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
        updateUser({ subscriptionTier: result.tier_id });
        toast({
          title: 'Welcome!',
          description: `You've been upgraded to ${SUBSCRIPTION_TIERS_NAMES[result.tier_id] || result.tier_id} with your discount code!`
        });
        navigate('/feed');
        return;
      }
      
      // If prorated upgrade (existing subscription modified)
      if (result.upgraded) {
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
        updateUser({ subscriptionTier: result.tier_id });
        toast({
          title: 'Upgrade Successful!',
          description: `You've been upgraded to ${SUBSCRIPTION_TIERS_NAMES[result.tier_id] || result.tier_id}. Any price difference is prorated.`
        });
        navigate('/feed');
        return;
      }
      
      if (result.checkout_url) {
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      toast({
        title: 'Checkout Error',
        description: error.response?.data?.detail || 'Failed to start checkout. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoadingTier(null);
    }
  };

  // A subscription only counts as the member's "current plan" when it's genuinely
  // active. Canceled/expired members are treated as free so they can re-subscribe.
  const subActive = ['active', 'canceling', 'past_due'].includes(currentUser?.subscriptionStatus);
  const currentTierInfo = subActive ? tiers.find(t => t.id === `tier_${currentUser?.subscriptionTier}`) : null;
  const currentTierLevel = currentTierInfo?.accessLevel ?? 0;

  const getButtonLabel = (tier, isCurrentTier, hasDiscount) => {
    if (isCurrentTier) return 'CURRENT PLAN';
    if (tier.isFree) return currentTierLevel > 0 ? 'CANCEL FROM PROFILE' : 'OPEN CASE';
    if (hasDiscount) return `PAY WITH CARD - SAVE $${appliedDiscount[tier.id].savings.toFixed(2)}`;
    if (currentTierLevel > 0 && tier.accessLevel > currentTierLevel) return 'UPGRADE WITH CARD';
    if (currentTierLevel > 0 && tier.accessLevel < currentTierLevel) return 'CHANGE PLAN';
    return 'PAY WITH CARD';
  };

  const getDisplayPrice = (tier) => {
    if (appliedDiscount && appliedDiscount[tier.id]) {
      return appliedDiscount[tier.id].discountedPrice;
    }
    return billingInterval === 'year' ? tier.annualPrice : tier.price;
  };

  // Crypto: redirect to NOWPayments hosted checkout
  const handleCryptoCheckout = async (tier) => {
    if (!currentUser) { goToLoginKeepingCode(); return; }
    setCryptoLoading(tier.tierKey);
    try {
      const result = await createCryptoCheckout(tier.tierKey, appliedDiscount?.[tier.id] ? discountCode : null);
      if (result.invoice_url) {
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
        window.location.href = result.invoice_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create crypto checkout', variant: 'destructive' });
      setCryptoLoading(null);
    }
  };

  // PayPal: redirect to PayPal approval
  const handlePayPalCheckout = async (tier) => {
    if (!currentUser) { goToLoginKeepingCode(); return; }
    setPaypalLoading(tier.tierKey);
    try {
      const result = await createPayPalSubscription(tier.tierKey, billingInterval, appliedDiscount?.[tier.id] ? discountCode : null);
      if (result.approval_url) {
        sessionStorage.removeItem(PROMO_STORAGE_KEY);
        window.location.href = result.approval_url;
      } else {
        throw new Error('No PayPal approval URL returned');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create PayPal checkout', variant: 'destructive' });
      setPaypalLoading(null);
    }
  };

  return (
    <motion.div 
      className="min-h-screen py-12 px-4 bg-black relative" 
      data-testid="tiers-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Badge className="mb-4 bg-amber-900/30 text-amber-200 border-amber-600 font-typewriter text-xs" variant="outline">
            SECURITY CLEARANCE LEVELS
          </Badge>
          <div className="inline-block mb-4">
            <div className="bg-amber-900/20 border-2 border-amber-600 px-8 py-3 inline-flex items-center gap-3">
              <Shield className="w-8 h-8 text-amber-500" />
              <h1 className="text-4xl md:text-5xl font-headline text-amber-500 tracking-widest" data-testid="tiers-title">
                CASE ACCESS LEVELS
              </h1>
            </div>
          </div>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto font-typewriter">
            All paid tiers include access to lower tier content. Upgrade or downgrade anytime.
          </p>
          <button
            type="button"
            onClick={() => navigate('/ranks')}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-600/60 bg-amber-900/20 px-5 py-2 text-sm font-typewriter text-amber-200 transition-colors hover:bg-amber-900/40 hover:text-amber-100"
            data-testid="tiers-loyalty-blurb"
          >
            <Award className="w-4 h-4 text-amber-400" />
            Paid members earn ranks &amp; loyalty rewards the longer they serve
            <span className="text-amber-500">→</span>
          </button>
          {currentUser && currentTierInfo && (
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-2 font-typewriter">Your current clearance:</p>
              <Badge 
                className="text-base px-4 py-1 font-headline tracking-wider" 
                style={{ backgroundColor: currentTierInfo.color, color: '#fff' }}
              >
                {currentTierInfo.name.toUpperCase()} CLEARANCE
              </Badge>
            </div>
          )}
        </motion.div>

        {/* Discount Code Section */}
        <motion.div 
          className="max-w-md mx-auto mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-5 h-5 text-amber-500" />
                <span className="font-headline text-amber-500 tracking-wider">HAVE A DISCOUNT CODE?</span>
              </div>
              
              {appliedDiscount ? (
                <div className="bg-green-900/20 border border-green-700/50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-mono text-green-400 font-bold">{discountCode.toUpperCase()}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleClearDiscount}
                      className="text-gray-400 hover:text-red-400 h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-green-400 mt-2 font-typewriter" data-testid="discount-applied-msg">
                    Discount applied to {Object.keys(appliedDiscount).length} tier(s)!
                  </p>
                  {!currentUser && (
                    <p className="text-xs text-amber-400 mt-2 font-typewriter" data-testid="discount-login-hint">
                      Your code is saved —{' '}
                      <button type="button" onClick={goToLoginKeepingCode} className="underline hover:text-amber-300" data-testid="discount-login-link">log in</button>
                      {' '}or{' '}
                      <button type="button" onClick={() => { sessionStorage.setItem(PROMO_STORAGE_KEY, discountCode); navigate('/signup'); }} className="underline hover:text-amber-300" data-testid="discount-signup-link">sign up</button>
                      {' '}to redeem it.
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="Enter code (e.g., SAVE20)"
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-mono uppercase flex-1"
                    data-testid="discount-code-input"
                  />
                  <Button 
                    onClick={() => handleApplyDiscount()}
                    disabled={validatingCode || !discountCode.trim()}
                    className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider"
                    data-testid="apply-discount-btn"
                  >
                    {validatingCode ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'APPLY'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Billing Interval Toggle */}
        {ANNUAL_BILLING_ENABLED && (
        <motion.div
          className="flex justify-center mb-8"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <div className="bg-gray-900/90 border-2 border-amber-900/30 rounded-lg p-1 inline-flex items-center gap-1" data-testid="billing-toggle">
            <button
              onClick={() => setBillingInterval('month')}
              className={`px-5 py-2 rounded-md font-headline tracking-wider text-sm transition-all ${
                billingInterval === 'month'
                  ? 'bg-amber-600 text-black'
                  : 'text-gray-400 hover:text-amber-500'
              }`}
              data-testid="billing-monthly-btn"
            >
              MONTHLY
            </button>
            <button
              onClick={() => setBillingInterval('year')}
              className={`relative px-5 py-2 rounded-md font-headline tracking-wider text-sm transition-all flex items-center gap-2 ${
                billingInterval === 'year'
                  ? 'bg-amber-600 text-black'
                  : 'text-amber-300 hover:text-amber-200 ring-2 ring-amber-500/70 animate-pulse-glow shadow-[0_0_14px_rgba(245,158,11,0.55)] hover:shadow-[0_0_22px_rgba(245,158,11,0.85)]'
              }`}
              data-testid="billing-annual-btn"
            >
              ANNUAL
              <span className={`text-xs px-2 py-0.5 rounded-full font-typewriter ${
                billingInterval === 'year' ? 'bg-black/30 text-amber-200' : 'bg-amber-500 text-black font-bold'
              }`}>
                SAVE {annualDiscount}%
              </span>
            </button>
          </div>
        </motion.div>
        )}

        {/* Loading State */}
        {loadingTiers ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* Tiers Grid */}
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {tiers.map((tier, index) => {
                const isCurrentTier = subActive && currentUser && tier.id === `tier_${currentUser.subscriptionTier}`;
                const isPopular = tier.id === 'tier_gold';
                const hasDiscount = appliedDiscount && appliedDiscount[tier.id];
                const displayPrice = getDisplayPrice(tier);

            return (
              <Card 
                key={tier.id} 
                className={`relative overflow-hidden transition-all hover:scale-105 bg-gray-900/90 border-2 backdrop-blur ${
                  isPopular ? 'border-amber-500 shadow-[0_0_30px_rgba(251,191,36,0.3)]' : 'border-gray-700 hover:border-amber-700/50'
                } ${isCurrentTier ? 'ring-2 ring-green-500' : ''} ${hasDiscount ? 'ring-2 ring-green-400' : ''}`}
                data-testid={`tier-card-${tier.name.toLowerCase()}`}
              >
                {isPopular && (
                  <div className="absolute top-0 right-0 bg-amber-600 text-black px-3 py-1 text-xs font-headline tracking-wider">
                    FULL ACCESS
                  </div>
                )}
                {isCurrentTier && (
                  <div className="absolute top-0 left-0 bg-green-600 text-white px-3 py-1 text-xs font-headline tracking-wider">
                    CURRENT
                  </div>
                )}
                {hasDiscount && !isCurrentTier && (
                  <div className="absolute top-0 left-0 bg-green-500 text-black px-3 py-1 text-xs font-headline tracking-wider">
                    DISCOUNT
                  </div>
                )}

                <CardHeader className="pt-6 pb-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 rounded-lg bg-amber-900/30 border border-amber-700" style={{ color: tier.color }}>
                      {getTierIcon(tier.name)}
                    </div>
                    <CardTitle className="text-2xl font-headline text-gray-100 tracking-wide">{tier.name}</CardTitle>
                  </div>
                  <div className="mb-2">
                    {tier.isFree ? (
                      <span className="text-4xl font-bold text-amber-500 font-headline">FREE</span>
                    ) : hasDiscount ? (
                      <div>
                        <span className="text-2xl font-bold text-gray-500 line-through font-headline mr-2">
                          ${tier.price}
                        </span>
                        <span className="text-4xl font-bold text-green-500 font-headline">
                          ${displayPrice.toFixed(2)}
                        </span>
                        <span className="text-base text-gray-500 font-typewriter">/month</span>
                      </div>
                    ) : billingInterval === 'year' && !tier.isFree ? (
                      <div>
                        <span className="text-4xl font-bold text-amber-500 font-headline">
                          ${(tier.annualPrice / 12).toFixed(2)}
                        </span>
                        <span className="text-base text-gray-500 font-typewriter">/month</span>
                        <div className="text-xs text-gray-500 font-typewriter mt-1">
                          Billed <span className="text-amber-400">${tier.annualPrice}/year</span>
                          <span className="text-green-500 ml-2">— Save ${((tier.price * 12) - tier.annualPrice).toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-amber-500 font-headline">
                          ${tier.price}
                        </span>
                        <span className="text-base text-gray-500 font-typewriter">/month</span>
                      </>
                    )}
                    {!tier.isFree && (
                      <p className="text-xs text-gray-400 font-typewriter mt-1" data-testid={`renews-note-${tier.id}`}>
                        Renews monthly. Cancel anytime.
                      </p>
                    )}
                  </div>
                  <CardDescription className="text-gray-400 font-typewriter text-sm">{tier.description}</CardDescription>
                </CardHeader>

                <CardContent className="pb-4">
                  <div className="space-y-3">
                    {tier.benefits.map((benefit, idx) => (
                      <div key={`${tier.id}-benefit-${idx}`} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="pt-4 flex flex-col gap-2">
                  <Button 
                    className={`w-full font-headline tracking-wider ${
                      hasDiscount
                        ? 'bg-green-600 hover:bg-green-700 text-white shadow-[0_0_15px_rgba(34,197,94,0.3)]'
                        : isPopular 
                          ? 'bg-amber-600 hover:bg-amber-700 text-black shadow-[0_0_15px_rgba(251,191,36,0.3)]' 
                          : isCurrentTier 
                            ? 'bg-green-800 hover:bg-green-700 border border-green-600' 
                            : 'bg-gray-800 hover:bg-gray-700 border border-amber-900'
                    }`}
                    onClick={() => isCurrentTier ? navigate('/feed') : handleSubscribe(tier.id)}
                    disabled={isCurrentTier || loadingTier === tier.id}
                    data-testid={`subscribe-btn-${tier.name.toLowerCase()}`}
                  >
                    {loadingTier === tier.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        PROCESSING...
                      </>
                    ) : (
                      getButtonLabel(tier, isCurrentTier, hasDiscount)
                    )}
                  </Button>
                  {!tier.isFree && !isCurrentTier && paypalEnabled && (
                    <Button
                      variant="outline"
                      className="w-full font-typewriter text-xs border-blue-800/50 text-blue-400 hover:bg-blue-900/20 hover:text-blue-300"
                      onClick={() => handlePayPalCheckout(tier)}
                      disabled={paypalLoading === tier.tierKey}
                      data-testid={`paypal-btn-${tier.name.toLowerCase()}`}
                    >
                      {paypalLoading === tier.tierKey ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> REDIRECTING...</>
                      ) : (
                        <>
                          <svg className="w-3.5 h-3.5 mr-1.5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797H9.603c-.564 0-1.04.408-1.13.964L7.076 21.337z"/></svg>
                          PAY WITH PAYPAL
                        </>
                      )}
                    </Button>
                  )}
                  {!tier.isFree && !isCurrentTier && billingInterval === 'year' && (
                    <Button
                      variant="outline"
                      className="w-full font-typewriter text-xs border-amber-900/50 text-amber-400 hover:bg-amber-900/20 hover:text-amber-300"
                      onClick={() => handleCryptoCheckout(tier)}
                      disabled={cryptoLoading === tier.tierKey}
                      data-testid={`crypto-btn-${tier.name.toLowerCase()}`}
                    >
                      {cryptoLoading === tier.tierKey ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> REDIRECTING...</>
                      ) : (
                        <><Bitcoin className="w-3.5 h-3.5 mr-1.5" /> PAY WITH CRYPTO</>
                      )}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </motion.div>

        {/* FAQ */}
        <motion.div 
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
        >
          <Card className="bg-gray-900/90 border-2 border-amber-900/30">
            <CardHeader>
              <CardTitle className="font-headline text-2xl text-amber-500 tracking-wider">FREQUENTLY ASKED QUESTIONS</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border-l-4 border-amber-600 pl-4">
                <h3 className="font-headline text-lg mb-1 text-gray-100 tracking-wide">Can I change my clearance level?</h3>
                <p className="text-sm text-gray-400 font-typewriter">
                  Yes! You can upgrade or downgrade your clearance at any time. Changes take effect immediately.
                </p>
              </div>
              <div className="border-l-4 border-amber-600 pl-4">
                <h3 className="font-headline text-lg mb-1 text-gray-100 tracking-wide">What payment methods do you accept?</h3>
                <p className="text-sm text-gray-400 font-typewriter">
                  We accept all major credit and debit cards (via Stripe) and PayPal. Memberships renew automatically each month at the listed price until you cancel. Charges appear on your statement as DEADLINE BY DARIA.
                </p>
              </div>
              <div className="border-l-4 border-amber-600 pl-4">
                <h3 className="font-headline text-lg mb-1 text-gray-100 tracking-wide">Do higher tiers include lower tier access?</h3>
                <p className="text-sm text-gray-400 font-typewriter">
                  Absolutely! Higher clearance levels include access to all lower-tier fictional case files. Gold members can access Bronze and Silver content.
                </p>
              </div>
              <div className="border-l-4 border-amber-600 pl-4">
                <h3 className="font-headline text-lg mb-1 text-gray-100 tracking-wide">Can I cancel anytime?</h3>
                <p className="text-sm text-gray-400 font-typewriter">
                  Yes. Go to your Profile, scroll to "Membership & Billing" and click "Cancel membership". Selecting the Free tier does not cancel billing. Your membership stops renewing and you keep access until the end of the period you've paid for. Because content is digital and delivered immediately, payments are non-refundable except for billing errors or duplicate charges, which we correct within 5 business days.
                </p>
              </div>
              <div className="border-l-4 border-green-600 pl-4">
                <h3 className="font-headline text-lg mb-1 text-gray-100 tracking-wide">How do I use a discount code?</h3>
                <p className="text-sm text-gray-400 font-typewriter">
                  Enter your discount code in the field above the tiers and click "Apply". Your savings will be shown on the applicable tier cards.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default TiersPage;
